import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DoCheck, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { getTileImages, Tile } from '../../types/tile.types';
import { TileImageCapabilities, TileImageService, TileImageSlot } from '../../services/tile-image.service';
import { TilePreviewComponent } from '../tile-preview/tile-preview.component';

@Component({
    selector: 'app-tile-image-editor', standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, SelectButtonModule, TextareaModule, TooltipModule, TilePreviewComponent],
    templateUrl: './tile-image-editor.component.html',
    styleUrls: ['./tile-image-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TileImageEditorComponent implements OnInit, OnChanges, OnDestroy, DoCheck {
    @Input({ required: true }) public tile!: Partial<Tile>;
    @Input() public scope: 'global' | 'tenant' | 'personal' = 'global';
    @Input() public active = true;
    @Output() public iconClick = new EventEmitter<void>();
    public slot: TileImageSlot = 'cover';
    public prompt = '';
    public busy = false;
    public error = '';
    public candidate?: Partial<Tile>;
    public capabilities?: TileImageCapabilities;
    public readonly slots = [
        { label: $localize`:@@tileImageCover:Titelbild`, value: 'cover' },
        { label: $localize`:@@tileImageIcon:Icon`, value: 'icon' },
    ];
    public readonly modes = [
        { label: $localize`:@@tileImageIcon:Icon`, value: 'logo' },
        { label: $localize`:@@tileImageCover:Titelbild`, value: 'cover' },
        { label: $localize`:@@tileImageBoth:Beides`, value: 'cover-icon' },
    ];
    private readonly images = inject(TileImageService);
    private readonly cdr = inject(ChangeDetectorRef);
    private operation?: AbortController;
    private destroyed = false;
    private baseImages?: ReturnType<typeof getTileImages>;

    public async ngOnInit(): Promise<void> {
        try { this.capabilities = await this.images.capabilities(); }
        catch { this.capabilities = undefined; }
        this.refresh();
    }

    public ngOnChanges(): void {
        this.cancel();
        this.candidate = undefined;
        this.restorePrompt();
    }

    public ngOnDestroy(): void { this.destroyed = true; this.cancel(); }

    public ngDoCheck(): void { this.refresh(); }

    public changeSlot(slot: TileImageSlot): void {
        this.cancel();
        this.slot = slot;
        this.candidate = undefined;
        this.error = '';
        this.restorePrompt();
    }

    public changeMode(mode: Tile['imageMode']): void {
        if (this.candidate) this.candidate.imageMode = mode;
        else {
            Object.assign(this.tile, getTileImages(this.tile), { imageUrl: '', imageMode: mode });
        }
    }

    public get candidateImage(): string {
        if (!this.candidate) return '';
        const images = getTileImages(this.candidate);
        return this.slot === 'icon' ? images.iconImageUrl : images.coverImageUrl;
    }

    public get currentImage(): string {
        const images = getTileImages(this.tile);
        return this.slot === 'icon' ? images.iconImageUrl : images.coverImageUrl;
    }

    public get storageKilobytes(): number {
        const images = getTileImages(this.candidate ?? this.tile);
        return Math.ceil((images.iconImageUrl.length + images.coverImageUrl.length) / 1024);
    }

    public async generate(): Promise<void> {
        if (this.busy || !this.active || this.scope !== 'global' || !this.capabilities?.available || !this.prompt.trim()) return;
        const prompt = this.prompt.trim();
        await this.propose(signal => this.images.generate(prompt, this.slot, this.tile._id, signal), prompt);
    }

    public async upload(event: Event): Promise<void> {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        input.value = '';
        if (!file || this.busy || !this.active) return;
        if (!['image/png', 'image/jpeg', 'image/webp', 'image/gif'].includes(file.type) || file.size > 10 * 1024 * 1024) {
            this.error = $localize`:@@tileImageUploadInvalid:Bitte PNG, JPEG, WebP oder GIF mit maximal 10 MB auswählen.`;
            return;
        }
        await this.propose(() => this.images.prepareUpload(file, this.slot), '');
    }

    public adopt(): void {
        if (!this.candidate || this.busy || !this.active) return;
        const current = getTileImages(this.tile);
        if (current.iconImageUrl !== this.baseImages?.iconImageUrl || current.coverImageUrl !== this.baseImages?.coverImageUrl) {
            this.error = $localize`:@@tileImageChanged:Die Kachelbilder wurden inzwischen geändert. Bitte einen neuen Vorschlag erstellen.`;
            this.candidate = undefined;
            return;
        }
        const { iconImageUrl, coverImageUrl, iconImagePrompt, coverImagePrompt, imageMode } = this.candidate;
        Object.assign(this.tile, { imageUrl: '', iconImageUrl, coverImageUrl, iconImagePrompt, coverImagePrompt, imageMode });
        this.candidate = undefined;
        this.restorePrompt();
    }

    public remove(): void {
        this.cancel();
        this.candidate = undefined;
        Object.assign(this.tile, getTileImages(this.tile), { imageUrl: '' });
        if (this.slot === 'icon') { this.tile.iconImageUrl = ''; this.tile.iconImagePrompt = ''; }
        else { this.tile.coverImageUrl = ''; this.tile.coverImagePrompt = ''; }
        this.restorePrompt();
    }

    public cancel(): void {
        this.operation?.abort();
        this.operation = undefined;
        this.busy = false;
    }

    private restorePrompt(): void {
        this.prompt = (this.slot === 'icon' ? this.tile?.iconImagePrompt : this.tile?.coverImagePrompt) ?? '';
    }

    private async propose(create: (signal: AbortSignal) => Promise<string>, prompt: string): Promise<void> {
        const controller = new AbortController();
        this.operation = controller;
        const tile = this.tile;
        const slot = this.slot;
        this.busy = true;
        this.error = '';
        try {
            const imageUrl = await create(controller.signal);
            if (controller.signal.aborted || this.destroyed || this.tile !== tile) return;
            const baseImages = getTileImages(tile);
            const candidate = await this.images.candidate(tile, slot, imageUrl, prompt);
            if (controller.signal.aborted || this.destroyed || this.tile !== tile) return;
            this.baseImages = baseImages;
            this.candidate = candidate;
        } catch (error) {
            if (!controller.signal.aborted && !this.destroyed) {
                const response = error as { error?: string | { message?: string }; message?: string };
                this.error = (typeof response.error === 'string' ? response.error : response.error?.message) || response.message || $localize`:@@tileGenerationFailed:Die Bildgenerierung wurde beendet. Bitte erneut versuchen.`;
            }
        } finally {
            if (this.operation === controller) { this.busy = false; this.operation = undefined; }
            this.refresh();
        }
    }

    private refresh(): void { if (!this.destroyed) this.cdr.detectChanges(); }
}
